import React, { useState, useEffect } from 'react'
import { FaHome, FaUser, FaHeart, FaGraduationCap, FaStar, FaBars, FaTimes, FaEnvelope } from 'react-icons/fa'
import { MdFamilyRestroom } from 'react-icons/md'
import { GiLovers } from 'react-icons/gi'

export default function Navbar() {
  const [activeSection, setActiveSection] = useState('hero')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  const navItems = [
    { id: 'hero', label: 'בית', icon: FaHome },
    { id: 'couples-counseling', label: 'ייעוץ זוגי', icon: GiLovers },
    { id: 'info', label: 'ייעוץ משפחתי', icon: MdFamilyRestroom },
    { id: 'lectures', label: 'הרצאות', icon: FaGraduationCap },
    { id: 'testimonials', label: 'המלצות', icon: FaStar },
    { id: 'study', label: 'לימודים', icon: FaHeart },
    { id: 'footer', label: 'יצירת קשר', icon: FaEnvelope }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => document.getElementById(item.id)).filter(Boolean)
      const scrollPosition = window.scrollY + 100
      
      // Update scrolled state for navbar background
      setIsScrolled(window.scrollY > 50)

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(navItems[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      setIsMobileMenuOpen(false) // Close mobile menu after clicking
    }
  }

  return (
    <>
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[150] transition-all duration-500 ease-out">
        <nav className={`backdrop-blur-xl rounded-full px-6 py-3 shadow-2xl w-[95vw] max-w-5xl transition-all duration-500 ease-out transform hover:scale-[1.02] ${
          isScrolled 
            ? 'bg-white/95 shadow-xl shadow-gray-900/25' 
            : 'bg-white/90 shadow-2xl shadow-gray-500/30'
        }`}>
          <div className="flex items-center justify-between w-full" dir="rtl">
            
            {/* Logo/Title with enhanced animation - Right side */}
            <button 
              onClick={() => scrollToSection('hero')}
              className="text-primary font-bold text-xl md:text-2xl hover:text-white p-4 transition-all duration-500 cursor-pointer transform hover:scale-110 hover:rotate-1 relative group whitespace-nowrap"
            >
              <span className="relative z-10">מלי דיין</span>
              <div className="absolute inset-0 bg-card rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300 ease-out -z-10"></div>
            </button>

            {/* Navigation Items - Desktop with enhanced animations - Left side */}
            <div className="hidden lg:flex items-center space-x-reverse space-x-3">
              {navItems.map((item, index) => {
                const Icon = item.icon
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`group flex items-center space-x-reverse space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 hover:-translate-y-1 relative overflow-hidden ${
                      activeSection === item.id
                        ? 'bg-card text-white shadow-lg shadow-card/50 scale-105'
                        : 'text-gray-700 hover:bg-card hover:text-primary hover:shadow-md'
                    }`}
                    style={{
                      animationDelay: `${index * 0.1}s`
                    }}
                  >
                    <span className="text-sm font-medium relative z-10 transition-all duration-300 group-hover:font-semibold">
                      {item.label}
                    </span>
                    <Icon className={`text-sm mr-2 relative z-10 transition-all duration-300 ${
                      activeSection === item.id ? 'animate-pulse' : 'group-hover:rotate-12 group-hover:scale-110'
                    }`} />
                    
                  </button>
                )
              })}
            </div>
            
            {/* Mobile Burger Menu with enhanced animation - Left side */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-3 rounded-xl text-gray-700  hover:bg-card   transition-all duration-300 transform hover:scale-110 hover:rotate-90 group"
              >
                <div className="relative">
                  <FaTimes className={`text-xl absolute transition-all duration-300 ${
                    isMobileMenuOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 rotate-180 scale-50'
                  }`} />
                  <FaBars className={`text-xl transition-all duration-300 ${
                    isMobileMenuOpen ? 'opacity-0 -rotate-180 scale-50' : 'opacity-100 rotate-0 scale-100'
                  }`} />
                </div>
              </button>
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Menu Overlay with enhanced animations */}
      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ease-out ${
        isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
      }`}>
        <div 
          className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`} 
          onClick={() => setIsMobileMenuOpen(false)} 
        />
        <div className={`absolute top-20 left-1/2 transform -translate-x-1/2 w-11/12 max-w-sm bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-primary/20 transition-all duration-500 ease-out ${
          isMobileMenuOpen 
            ? 'translate-y-0 scale-100 opacity-100' 
            : '-translate-y-10 scale-95 opacity-0'
        }`}>
          <div className="p-6" dir="rtl">
            <div className="space-y-2">
              {navItems.map((item, index) => {
                const Icon = item.icon
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 hover:-translate-x-2 group relative overflow-hidden ${
                      activeSection === item.id
                        ? 'bg-card text-white shadow-lg'
                        : 'text-gray-700 hover:bg-gradient-to-r hover:bg-card hover:text-primary hover:shadow-md'
                    }`}
                    style={{
                      animationDelay: `${(index + 1) * 0.1}s`,
                      animation: isMobileMenuOpen ? 'slideInRight 0.5s ease-out forwards' : ''
                    }}
                  >
                    <span className="text-sm sm:text-lg font-medium relative z-10 transition-all duration-300 group-hover:font-semibold">
                      {item.label}
                    </span>
                    <Icon className={`text-sm sm:text-lg mr-3 relative z-10 transition-all duration-300 ${
                      activeSection === item.id ? 'animate-bounce' : 'group-hover:rotate-12 group-hover:scale-125'
                    }`} />
                    
                    {/* Ripple effect on click */}
                    <div className="absolute inset-0 bg-white/20 scale-0 rounded-full transition-transform duration-300 group-active:scale-100"></div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for mobile menu animations */}
      <style jsx>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  )
} 